import { cn } from "@/lib/cn";
import {
    FloatingFocusManager,
    FloatingPortal,
    autoUpdate,
    flip,
    size as floatingSize,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useMergeRefs,
    useRole,
    useTransitionStatus,
    type Placement,
} from "@floating-ui/react";
import * as React from "react";

interface PopoverOptions {
  initialOpen?: boolean;
  placement?: Placement;
  strategy?: "fixed" | "absolute";
  modal?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  stayOnScroll?: boolean;
  excludeSelectors?: string[];
  closeOnFocusOut?: boolean;
  triggerWidth?: boolean;
  /** Gap from the trigger; the object form also shifts along the cross axis (floating-ui OffsetOptions). */
  offset?: number | { mainAxis?: number; crossAxis?: number };
  /**
   * Expose the height left between the trigger side and the viewport edge as
   * `--popover-available-height` on the content element, so tall content can
   * cap itself (`max-height: min(<design cap>, var(--popover-available-height))`)
   * and scroll inside instead of running off the screen.
   */
  fitViewport?: boolean;
}

function usePopover({
  initialOpen = false,
  placement = "bottom",
  strategy = "fixed",
  modal,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  stayOnScroll = false,
  closeOnFocusOut,
  excludeSelectors = [],
  triggerWidth,
  offset: offsetOption = 5,
  fitViewport = false,
}: PopoverOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
  const [labelId, setLabelId] = React.useState<string | undefined>();
  const [descriptionId, setDescriptionId] = React.useState<
    string | undefined
  >();

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: !stayOnScroll,
      });
    },
    strategy,
    middleware: [
      offset(offsetOption),
      flip({
        crossAxis: placement.includes("-"),
        fallbackAxisSideDirection: "end",
        padding: 5,
      }),
      shift({ padding: 5 }),
      floatingSize({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            ...(triggerWidth ? { width: `${rects.reference.width}px` } : {}),
          });
          if (fitViewport) {
            elements.floating.style.setProperty(
              "--popover-available-height",
              `${Math.max(0, Math.floor(availableHeight))}px`,
            );
          }
        },
        padding: 10,
      }),
    ],
  });

  const context = data.context;

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePressEvent: "mousedown",
    ancestorScroll: !stayOnScroll,
    referencePress: false,
    outsidePress: (event) => {
      return !excludeSelectors.some((selector) => {
        const elements = document.querySelectorAll(selector);
        return Array.from(elements).some((element) =>
          element.contains(event.target as Node),
        );
      });
    },
  });
  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      modal,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
      closeOnFocusOut,
    }),
    [
      open,
      setOpen,
      interactions,
      data,
      modal,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
      closeOnFocusOut,
    ],
  );
}

type ContextType =
  | (ReturnType<typeof usePopover> & {
      setLabelId: React.Dispatch<React.SetStateAction<string | undefined>>;
      setDescriptionId: React.Dispatch<
        React.SetStateAction<string | undefined>
      >;
    })
  | null;

const PopoverContext = React.createContext<ContextType>(null);

const usePopoverContext = () => {
  const context = React.useContext(PopoverContext);

  if (context == null) {
    throw new Error("Popover components must be wrapped in <Popover />");
  }

  return context;
};

/**
 * Returns a function that closes the enclosing Popover, or `undefined` when
 * the caller is not rendered inside one (e.g. a shared component used in both
 * popover and non-popover layouts). Never throws.
 */
export const usePopoverClose = (): (() => void) | undefined => {
  const context = React.useContext(PopoverContext);
  if (context == null) return undefined;
  return () => context.setOpen(false);
};

function Popover({
  children,
  modal = false,
  ...restOptions
}: {
  children: React.ReactNode;
} & PopoverOptions) {
  const popover = usePopover({ modal, ...restOptions });
  return (
    <PopoverContext.Provider value={popover}>
      {children}
    </PopoverContext.Provider>
  );
}

interface PopoverTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

const PopoverTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & PopoverTriggerProps
>(function PopoverTrigger(
  { children, asChild = false, className, ...props },
  propRef,
) {
  const context = usePopoverContext();
  const childrenRef = (children as any).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...(children.props as any),
        "data-state": context.open ? "open" : "closed",
      }),
    );
  }

  return (
    <div
      ref={ref}
      data-state={context.open ? "open" : "closed"}
      className={cn(
        "tw-block tw-border-none tw-bg-[transparent] !tw-outline-none focus-visible:!tw-outline-none active:!tw-outline-none",
        className,
      )}
      {...context.getReferenceProps(props)}
    >
      {children}
    </div>
  );
});

const PopoverContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement> & { portal?: boolean }
>(function PopoverContent(
  { style, className, portal = true, ...props },
  propRef,
) {
  "use no memo";
  const {
    closeOnFocusOut,
    context: floatingContext,
    ...context
  } = usePopoverContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);
  const { isMounted, status } = useTransitionStatus(floatingContext, {
    duration: 150,
  });

  if (!isMounted) return null;

  return (
    <Portal portal={portal}>
      <FloatingFocusManager
        context={floatingContext}
        closeOnFocusOut={closeOnFocusOut}
        modal={context.modal}
        initialFocus={-1}
      >
        <div
          className={cn(
            "tw-z-[9999] tw-border-none tw-bg-[transparent] tw-outline-none",
            "tw-transition-opacity tw-duration-150",
            status === "open" ? "tw-opacity-100" : "tw-opacity-0",
            className,
          )}
          ref={ref}
          style={{ ...context.floatingStyles, ...style }}
          aria-labelledby={context.labelId}
          aria-describedby={context.descriptionId}
          {...context.getFloatingProps(props)}
        >
          {props.children}
        </div>
      </FloatingFocusManager>
    </Portal>
  );
});

const Portal = ({
  children,
  portal = true,
}: {
  children: React.ReactNode;
  portal?: boolean;
}) => {
  if (portal) {
    return <FloatingPortal>{children}</FloatingPortal>;
  }

  return <>{children}</>;
};

const PopoverClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function PopoverClose(props, ref) {
  const { setOpen } = usePopoverContext();
  return (
    <button
      type="button"
      ref={ref}
      {...props}
      className="tw-border-none tw-bg-[transparent] tw-outline-none"
      onClick={(event) => {
        props.onClick?.(event);
        setOpen(false);
      }}
    />
  );
});

Popover.Trigger = PopoverTrigger;
Popover.Content = PopoverContent;
Popover.Close = PopoverClose;

export default Popover;
